import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, Loader2, Lock, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useActiveCompany, pickActiveYear } from "../../context/ActiveCompanyContext";
import { useScenarioMode } from "../../context/ScenarioModeContext";
import { ScenarioBanner, ScenarioToggle } from "../../components/planning/ScenarioModeControls";
import PageHeader from "../../components/PageHeader";
import EmptyState from "../../components/EmptyState";
import AssignmentPanel from "../../components/planning/AssignmentPanel";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../components/ui/tooltip";

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
const fmtSEK = (v) => (v == null ? "—" : new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 }).format(Math.round(v)));
const fmtH = (v) => (v == null ? "—" : new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 1 }).format(v));
const fmtP = (v) => (v == null ? "—" : `${new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 1 }).format(v)} %`);
const g = (obj, m) => obj?.[m] ?? obj?.[String(m)];

function monthInRange(year, month, start, end) {
  if (start) {
    const sy = Number(start.slice(0, 4));
    const sm = Number(start.slice(5, 7));
    if (year < sy || (year === sy && month < sm)) return false;
  }
  if (end) {
    const ey = Number(end.slice(0, 4));
    const em = Number(end.slice(5, 7));
    if (year > ey || (year === ey && month > em)) return false;
  }
  return true;
}

// Recompute all derived values client-side from raw inputs (mirrors backend calculations).
function recompute(data) {
  const wh = data.working_hours;
  const year = data.year;
  const summary = {};
  for (let m = 1; m <= 12; m++) {
    const prev = g(data.summary, m) || {};
    summary[m] = {
      employed_headcount: prev.employed_headcount ?? 0,
      subcontractor_count: prev.subcontractor_count ?? 0,
      missing_terms: prev.missing_terms ?? [],
      billable_hours: 0,
      tm_revenue: 0,
      capacity: 0,
      empBillable: 0,
      company_utilization: null,
      average_rate: null,
    };
  }
  const consultants = data.consultants.map((c) => {
    const isSub = c.person.engagement_type === "SUBCONTRACTOR";
    const monthly = {};
    for (let m = 1; m <= 12; m++) monthly[m] = { utilization: 0, billable_hours: 0, revenue: 0 };
    const assignments = c.assignments.map((a) => {
      const months = {};
      for (let m = 1; m <= 12; m++) {
        const cell = { ...g(a.months, m) };
        const available = g(wh, m);
        const util = cell.forecast_utilization;
        const hours = available != null && util != null ? (available * util) / 100 : 0;
        const rev = hours * (cell.hourly_sales_rate || 0);
        const subCost = isSub ? hours * (cell.subcontractor_hourly_cost_rate || 0) : null;
        cell.billable_hours = hours;
        cell.revenue = rev;
        cell.subcontractor_cost = subCost;
        cell.contribution_per_hour =
          isSub && cell.hourly_sales_rate != null && cell.subcontractor_hourly_cost_rate != null
            ? cell.hourly_sales_rate - cell.subcontractor_hourly_cost_rate
            : null;
        cell.contribution_margin = isSub && rev ? ((rev - subCost) / rev) * 100 : null;
        if (cell.actual && available) {
          cell.actual = { ...cell.actual, actual_utilization: (cell.actual.billed_hours / available) * 100 };
        }
        const mh = cell.closed ? cell.actual?.billed_hours ?? 0 : hours;
        const mr = cell.closed ? cell.actual?.actual_revenue ?? 0 : rev;
        monthly[m].utilization += util || 0;
        monthly[m].billable_hours += mh;
        monthly[m].revenue += mr;
        summary[m].billable_hours += mh;
        summary[m].tm_revenue += mr;
        months[m] = cell;
      }
      return { ...a, months };
    });
    if (!isSub) {
      for (let m = 1; m <= 12; m++) {
        const pct = g(c.employment_pct, m);
        if (pct == null) continue;
        if (!monthInRange(year, m, c.person.start_date, c.person.end_date)) continue;
        const available = g(wh, m);
        if (available == null) continue;
        summary[m].capacity += (available * pct) / 100;
        summary[m].empBillable += monthly[m].billable_hours;
      }
    }
    return { ...c, monthly, assignments };
  });
  for (let m = 1; m <= 12; m++) {
    const s = summary[m];
    s.average_rate = s.billable_hours ? s.tm_revenue / s.billable_hours : null;
    s.company_utilization = s.capacity ? (s.empBillable / s.capacity) * 100 : null;
  }
  return { ...data, consultants, summary };
}

function UtilCell({ cell, canEdit, onCommit, testId }) {
  const [draft, setDraft] = useState(null);
  const [state, setState] = useState(""); // "" | "saving" | "saved" | "error"

  if (cell.closed) {
    return (
      <td className="px-1 py-1 bg-slate-100/70" title="Closed period — actual" data-testid={testId}>
        <div className="h-8 flex items-center justify-center text-sm text-slate-700 font-medium">
          {cell.actual ? fmtP(cell.actual.actual_utilization) : "—"}
        </div>
      </td>
    );
  }
  if (!cell.in_scope) {
    return (
      <td className="px-1 py-1" data-testid={testId}>
        <div className="h-8 flex items-center justify-center text-xs text-slate-300">·</div>
      </td>
    );
  }
  if (!canEdit) {
    return (
      <td className="px-1 py-1" data-testid={testId}>
        <div className="h-8 flex items-center justify-center text-sm text-slate-700">
          {cell.forecast_utilization ?? "—"}
        </div>
      </td>
    );
  }
  const commit = async () => {
    if (draft == null) return;
    const v = draft === "" ? null : Number(draft);
    if (v === (cell.forecast_utilization ?? null)) {
      setDraft(null);
      return;
    }
    setState("saving");
    const ok = await onCommit(v);
    setState(ok ? "saved" : "error");
    if (ok) setDraft(null);
    setTimeout(() => setState(""), 1200);
  };
  return (
    <td className="px-1 py-1" data-testid={testId}>
      <div className="relative">
        <input
          type="number"
          min="0"
          className={`h-8 w-full text-center text-sm rounded-md border bg-white outline-none transition-colors
            ${state === "error" ? "border-[#B91C1C]" : "border-transparent hover:border-slate-300 focus:border-[#0052CC]"}
            ${state === "saved" ? "border-[#047857]" : ""}`}
          value={draft ?? cell.forecast_utilization ?? ""}
          placeholder="0"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.target.blur();
          }}
          data-testid={`${testId}-input`}
        />
        {state === "saving" && (
          <Loader2 className="h-3 w-3 animate-spin text-slate-400 absolute right-1 top-2.5" />
        )}
      </div>
    </td>
  );
}

export default function ResourcePlanningPage() {
  const { user, isAdmin } = useAuth();
  const { scenarioMode } = useScenarioMode();
  const canEdit = isAdmin || user?.role === "COMPANY_MANAGER";

  const [companies, setCompanies] = useState([]);
  const { companyId, setCompanyId, year, setYear } = useActiveCompany();
  const [years, setYears] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [assignFilter, setAssignFilter] = useState("ALL");
  const [customerFilter, setCustomerFilter] = useState("ALL");
  const [customers, setCustomers] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [vatRates, setVatRates] = useState([]);
  const [panel, setPanel] = useState(null); // {assignmentPayload, person}
  const [addFor, setAddFor] = useState(null); // person for new assignment
  const [assignForm, setAssignForm] = useState({ name: "", customer_id: "", start_date: "", end_date: "" });

  useEffect(() => {
    Promise.all([api.get("/companies"), api.get("/customers"), api.get("/payment-terms"), api.get("/vat/rates")])
      .then(([c, cu, pt, vr]) => {
        setCompanies(c.data);
        setCustomers(cu.data);
        setPaymentTerms(pt.data);
        setVatRates(vr.data);
        if (c.data.length > 0 && !c.data.some((co) => co.id === companyId)) setCompanyId(c.data[0].id);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId) return;
    setData(null);
    api
      .get(`/companies/${companyId}/periods/years`)
      .then((res) => {
        setYears(res.data);
        const pick = pickActiveYear(res.data, year);
        if (pick !== year) setYear(pick);
      })
      .catch((e) => toast.error(apiError(e)));
  }, [companyId, year]);

  const loadPlanning = async () => {
    if (!companyId || !year) return;
    setLoading(true);
    try {
      const res = await api.get(scenarioMode ? "/scenario-outcome/resource-planning" : "/resource-planning", { params: { company_id: companyId, year } });
      setData(recompute(res.data));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlanning();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, year, scenarioMode]);

  const commitUtil = async (assignmentId, m, v) => {
    try {
      await api.put("/assignment-months", {
        assignment_id: assignmentId,
        year,
        month: m,
        forecast_utilization: v,
      });
      setData((prev) => {
        const next = {
          ...prev,
          consultants: prev.consultants.map((c) => ({
            ...c,
            assignments: c.assignments.map((a) =>
              a.assignment.id === assignmentId
                ? { ...a, months: { ...a.months, [m]: { ...g(a.months, m), forecast_utilization: v } } }
                : a
            ),
          })),
        };
        return recompute(next);
      });
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    }
  };

  const createAssignment = async () => {
    try {
      await api.post("/assignments", {
        company_id: companyId,
        person_id: addFor.id,
        customer_id: assignForm.customer_id,
        name: assignForm.name,
        start_date: assignForm.start_date || null,
        end_date: assignForm.end_date || null,
      });
      toast.success("Assignment created");
      setAddFor(null);
      setAssignForm({ name: "", customer_id: "", start_date: "", end_date: "" });
      await loadPlanning();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.consultants.filter((c) => {
      if (typeFilter !== "ALL" && c.person.engagement_type !== typeFilter) return false;
      if (assignFilter === "ASSIGNED" && c.assignments.length === 0) return false;
      if (assignFilter === "UNASSIGNED" && c.assignments.length > 0) return false;
      if (customerFilter !== "ALL" && !c.assignments.some((a) => a.assignment.customer_id === customerFilter)) return false;
      if (q) {
        const inName = c.person.name.toLowerCase().includes(q);
        const inAssignment = c.assignments.some(
          (a) =>
            a.assignment.name.toLowerCase().includes(q) ||
            (a.customer_name || "").toLowerCase().includes(q)
        );
        if (!inName && !inAssignment) return false;
      }
      return true;
    });
  }, [data, search, typeFilter, assignFilter, customerFilter]);

  const wh = data?.working_hours || {};

  return (
    <div data-testid="resource-planning-page">
      <PageHeader
        title="Resource Planning"
        subtitle="Consultant Assignment × Month — the operational planning surface"
        actions={
          <div className="flex items-center gap-2">
            <ScenarioToggle />
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="w-56" data-testid="rp-company-select">
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {years.length > 0 && (
              <Select value={year ? String(year) : ""} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="w-28" data-testid="rp-year-select">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)} data-testid={`rp-year-${y}`}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        }
      />
      <ScenarioBanner meta={data?.scenario_meta} />

      {companyId && years.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="rp-no-years">
          <EmptyState
            title="No financial years created"
            description="Create a financial year under Administration → Companies before planning."
            testId="rp-no-years-state"
          />
        </div>
      )}

      {year && (
        <>
          <div className="flex items-center gap-2 mb-4" data-testid="rp-filters">
            <div className="relative">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-2.5" />
              <Input
                className="pl-9 w-64 bg-white"
                placeholder="Search consultant, customer, assignment…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="rp-search-input"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-40 bg-white" data-testid="rp-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All types</SelectItem>
                <SelectItem value="EMPLOYEE">Employees</SelectItem>
                <SelectItem value="SUBCONTRACTOR">Subcontractors</SelectItem>
              </SelectContent>
            </Select>
            <Select value={assignFilter} onValueChange={setAssignFilter}>
              <SelectTrigger className="w-40 bg-white" data-testid="rp-assign-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All</SelectItem>
                <SelectItem value="ASSIGNED">Assigned</SelectItem>
                <SelectItem value="UNASSIGNED">Unassigned</SelectItem>
              </SelectContent>
            </Select>
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger className="w-48 bg-white" data-testid="rp-customer-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All customers</SelectItem>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="rp-grid">
            {loading && (
              <div className="py-16 flex justify-center" data-testid="rp-loading">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            )}
            {!loading && data && (
              <table className="w-full min-w-[1100px] border-collapse">
                <thead>
                  <tr className="bg-secondary/50 border-b border-slate-200">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-3 sticky left-0 bg-secondary/50 min-w-56 z-10">Consultant</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-2 w-28">Type</th>
                        {MONTHS.map((label, i) => {
                      const closed = g(data.periods, i + 1)?.status === "CLOSED";
                      const whMissing = (data.working_hours_missing || []).includes(i + 1);
                      return (
                        <th key={label} className={`text-center text-xs font-semibold uppercase tracking-wider py-2.5 px-1 w-20 ${closed ? "text-slate-400 bg-slate-100/70" : "text-slate-500"}`}>
                          <div className="flex items-center justify-center gap-1">
                            {closed && <Lock className="h-3 w-3" />}
                            {label}
                            {whMissing && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span tabIndex={0} className="inline-flex" data-testid={`wh-missing-${i + 1}`}>
                                    <AlertTriangle className="h-3 w-3 text-[#B45309]" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>Missing Working Hours for {label} {data.year} — configure under Administration → Working Hours</TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                          <div className="text-[10px] font-normal normal-case text-slate-400" title="Calendar available hours">
                            {g(wh, i + 1) != null ? `${g(wh, i + 1)}h` : "—"}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => {
                    const isSub = c.person.engagement_type === "SUBCONTRACTOR";
                    const isOpen = expanded[c.person.id];
                    const unassigned = c.assignments.length === 0;
                    return [
                      <tr
                        key={c.person.id}
                        className="border-b border-slate-200 hover:bg-accent/40 cursor-pointer transition-colors"
                        onClick={() => setExpanded({ ...expanded, [c.person.id]: !isOpen })}
                        data-testid={`consultant-row-${c.person.id}`}
                      >
                        <td className="py-2.5 px-3 sticky left-0 bg-white z-10">
                          <div className="flex items-center gap-2">
                            {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                            <span className="text-sm font-semibold text-slate-900">{c.person.name}</span>
                            {c.person.scenario && (
                              <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-[#FFF7ED] text-[#C2410C] border border-[#FDBA74]" data-testid={`rp-scenario-badge-${c.person.id}`}>Scenario</span>
                            )}
                            {unassigned && !isSub && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span tabIndex={0} className="inline-flex" data-testid={`unassigned-flag-${c.person.id}`}>
                                    <AlertTriangle className="h-3.5 w-3.5 text-[#B45309]" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>No assignments — 0% utilization (attention, not an error)</TooltipContent>
                              </Tooltip>
                            )}
                            {c.missing_terms_months.length > 0 && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span tabIndex={0} className="inline-flex" data-testid={`missing-terms-flag-${c.person.id}`}>
                                    <AlertTriangle className="h-3.5 w-3.5 text-[#B91C1C]" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Missing Employment Terms for {c.missing_terms_months.map((m) => MONTHS[m - 1]).join(", ")} {data.year} — excluded from Company Utilization. Resolve under Administration → People → Terms.
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                        <td className="py-2.5 px-2">
                          <span className="text-xs text-slate-500">{isSub ? "Subcontractor" : "Employee"}</span>
                        </td>
                        {MONTHS.map((_, i) => {
                          const m = i + 1;
                          const mv = g(c.monthly, m) || { utilization: 0 };
                          const closed = g(data.periods, m)?.status === "CLOSED";
                          const util = mv.utilization;
                          const over = util > 100;
                          return (
                            <td
                              key={m}
                              className={`py-2.5 px-1 text-center text-sm font-semibold ${closed ? "bg-slate-100/70" : ""}`}
                              data-testid={`consultant-util-${c.person.id}-${m}`}
                            >
                              <span className={over ? "text-[#B45309]" : "text-slate-800"}>
                                {fmtP(util)}
                              </span>
                              {over && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span tabIndex={0} className="inline-flex ml-1" data-testid={`over-util-flag-${c.person.id}-${m}`}>
                                      <AlertTriangle className="h-3 w-3 text-[#B45309]" />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>Utilization above 100% in {MONTHS[m - 1]} — allowed, review</TooltipContent>
                                </Tooltip>
                              )}
                            </td>
                          );
                        })}
                      </tr>,
                      isOpen &&
                        c.assignments.map((a) => (
                          <tr key={a.assignment.id} className="border-b border-slate-100 bg-[#FAFBFD]" data-testid={`assignment-row-${a.assignment.id}`}>
                            <td className="py-1 px-3 sticky left-0 bg-[#FAFBFD] z-10">
                              <div className="flex items-center gap-2 pl-6">
                                <button
                                  className="text-sm text-[#0052CC] font-medium hover:underline text-left"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (a.assignment.scenario) return; // scenario rows are hypothetical — no edit panel
                                    setPanel({ payload: a, person: c.person });
                                  }}
                                  data-testid={`open-assignment-${a.assignment.id}`}
                                >
                                  ↳ {a.assignment.name}
                                </button>
                                <span className="text-xs text-slate-400">{a.customer_name}</span>
                              </div>
                            </td>
                            <td className="py-1 px-2" />
                            {MONTHS.map((_, i) => {
                              const m = i + 1;
                              const cell = g(a.months, m) || {};
                              return (
                                <UtilCell
                                  key={m}
                                  cell={cell}
                                  canEdit={canEdit}
                                  onCommit={(v) => commitUtil(a.assignment.id, m, v)}
                                  testId={`util-cell-${a.assignment.id}-${m}`}
                                />
                              );
                            })}
                          </tr>
                        )),
                      isOpen && canEdit && !c.person.scenario && (
                        <tr key={`add-${c.person.id}`} className="border-b border-slate-100 bg-[#FAFBFD]">
                          <td className="py-1.5 px-3 sticky left-0 bg-[#FAFBFD] z-10" colSpan={14}>
                            <button
                              className="pl-6 text-xs font-semibold text-[#0052CC] hover:underline flex items-center gap-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAddFor(c.person);
                              }}
                              data-testid={`add-assignment-${c.person.id}`}
                            >
                              <Plus className="h-3 w-3" /> Add assignment
                            </button>
                          </td>
                        </tr>
                      ),
                    ];
                  })}
                </tbody>
                {data && filtered.length > 0 && (
                  <tfoot data-testid="rp-summary">
                    {[
                      { key: "employed_headcount", label: "Employed consultants", fmt: (v) => v ?? 0, testId: "summary-headcount" },
                      { key: "company_utilization", label: "Company utilization", fmt: fmtP, testId: "summary-utilization" },
                      { key: "billable_hours", label: "Billable hours", fmt: fmtH, testId: "summary-hours" },
                      { key: "tm_revenue", label: "T&M revenue", fmt: (v) => `${fmtSEK(v)} kr`, testId: "summary-revenue" },
                      { key: "average_rate", label: "Average rate", fmt: (v) => (v == null ? "N/A" : `${fmtSEK(v)} kr/h`), testId: "summary-rate" },
                    ].map((row) => (
                      <tr key={row.key} className="border-t border-slate-200 bg-slate-50/60">
                        <td className="py-1.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider sticky left-0 bg-secondary/50 z-10" colSpan={2}>
                          {row.label}
                        </td>
                        {MONTHS.map((_, i) => {
                          const m = i + 1;
                          const s = g(data.summary, m) || {};
                          return (
                            <td key={m} className="py-1.5 px-1 text-center text-xs font-semibold text-slate-700" data-testid={`${row.testId}-${m}`}>
                              {row.fmt(s[row.key])}
                              {row.key === "company_utilization" && (s.missing_terms || []).length > 0 && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span tabIndex={0} className="inline-flex ml-1" data-testid={`summary-missing-terms-${m}`}>
                                      <AlertTriangle className="h-3 w-3 text-[#B91C1C]" />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Missing Employment Terms for {s.missing_terms.join(", ")} in {MONTHS[m - 1]} {data.year} — capacity excluded from Company Utilization
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {row.key === "company_utilization" && s.missing_working_hours && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span tabIndex={0} className="inline-flex ml-1" data-testid={`summary-missing-wh-${m}`}>
                                      <AlertTriangle className="h-3 w-3 text-[#B45309]" />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>Missing Working Hours for {MONTHS[m - 1]} {data.year} — capacity cannot be calculated</TooltipContent>
                                </Tooltip>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tfoot>
                )}
              </table>
            )}
            {!loading && data && filtered.length === 0 && (
              <EmptyState title="No consultants match" description="Adjust filters or add people under Administration → People." testId="rp-empty" />
            )}
          </div>
        </>
      )}

      <AssignmentPanel
        open={!!panel}
        onClose={() => {
          setPanel(null);
          loadPlanning();
        }}
        payload={
          panel
            ? {
                ...panel.payload,
                person: panel.person,
                year,
                paymentTerms,
                vatRates,
                workingHours: wh,
              }
            : null
        }
        canEdit={canEdit}
      />

      <Dialog open={!!addFor} onOpenChange={(v) => !v && setAddFor(null)}>
        <DialogContent data-testid="assignment-dialog">
          <DialogHeader>
            <DialogTitle>New assignment — {addFor?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Assignment name</Label>
              <Input value={assignForm.name} onChange={(e) => setAssignForm({ ...assignForm, name: e.target.value })} data-testid="assignment-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Customer</Label>
              <Select value={assignForm.customer_id} onValueChange={(v) => setAssignForm({ ...assignForm, customer_id: v })}>
                <SelectTrigger data-testid="assignment-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Start date</Label>
                <Input type="date" value={assignForm.start_date} onChange={(e) => setAssignForm({ ...assignForm, start_date: e.target.value })} data-testid="assignment-start-input" />
              </div>
              <div className="space-y-1.5">
                <Label>End date</Label>
                <Input type="date" value={assignForm.end_date} onChange={(e) => setAssignForm({ ...assignForm, end_date: e.target.value })} data-testid="assignment-end-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddFor(null)} data-testid="assignment-cancel-button">Cancel</Button>
            <Button onClick={createAssignment} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="assignment-save-button">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
